/**
 * Response and request shapes of the API (apps/api controllers + zod schemas are the contract).
 * Money, quantities and rates are decimal strings ("100.00000"); dates are 'YYYY-MM-DD';
 * timestamps are ISO strings.
 */

export type Locale = 'ar' | 'en';
export type EtaEnvironment = 'mock' | 'preprod' | 'production';

export interface ApiIssue {
  path: string;
  issue: string;
  params?: Record<string, string | number>;
}

export interface Page<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
}

// ----------------------------------------------------------------------------- identity
export interface Me {
  user: { id: string; email: string; displayName: string; locale: string };
  organizations: OrganizationSummary[];
}

export interface OrganizationSummary {
  id: string;
  name: string;
  etaEnvironment: EtaEnvironment;
  status: string;
  role: string;
}

export interface Session {
  userId: string;
  csrfToken: string;
}

// ----------------------------------------------------------------------------- organization
export interface Organization {
  id: string;
  code: string | null;
  name: string;
  legalName: string | null;
  rin: string | null;
  country: string;
  baseCurrency: string;
  fiscalYearStartMonth: number;
  etaEnvironment: EtaEnvironment;
  email: string | null;
  phone: string | null;
  status: string;
  version: number;
  /**
   * WP-1b.4: how this deployment sends the buyer's e-mail copy. `file` saves it on the server (dev,
   * tests, the working preview): the editor and the confirmation say it is a test message.
   */
  emailTransport?: EmailTransport;
}

export type EmailTransport = 'file' | 'smtp';

/** WP-1b.4: the buyer's e-mail copy of an issued document (derived from its outbox row). */
export interface DeliveryView {
  id: string;
  status: 'queued' | 'sending' | 'sent' | 'failed';
  transport: EmailTransport;
  recipient: string;
  attempts: number;
  requestedAt: string;
  lastAttemptAt: string | null;
  sentAt: string | null;
  nextAttemptAt: string | null;
  error: { code: string; message: string | null } | null;
  /** Queued while the Tax Authority has not validated the document yet. */
  waitingForEta: boolean;
}

export interface Access {
  role: string;
  permissions: string[];
}

/** org.branches rows are returned as stored (snake_case). */
export interface Branch {
  id: string;
  eta_branch_id: string;
  name: string;
  country: string;
  governate: string;
  region_city: string;
  street: string;
  building_number: string;
  postal_code: string | null;
  floor: string | null;
  room: string | null;
  landmark: string | null;
  additional_information: string | null;
  is_default: boolean;
  status: string;
  version: number;
}

export interface BranchInput {
  etaBranchId: string;
  name: string;
  country: string;
  governate: string;
  regionCity: string;
  street: string;
  buildingNumber: string;
  postalCode?: string;
  floor?: string;
  room?: string;
  landmark?: string;
  additionalInformation?: string;
  isDefault?: boolean;
}

export interface OrganizationTax {
  id: string;
  taxType: string;
  taxSubtype: string;
  rate: string | null;
  nameAr: string;
  nameEn: string;
  isActive: boolean;
  kind: 'rate' | 'fixed';
  version: number;
}

// ----------------------------------------------------------------------------- reference data
export interface UnitType {
  code: string;
  nameEn: string;
  nameAr: string | null;
  ambiguous: boolean;
}

export interface ActivityCode {
  code: string;
  nameEn: string;
  nameAr: string | null;
}

export interface TaxSubtype {
  code: string;
  taxType: string;
  kind: 'rate' | 'fixed';
  nameEn: string;
  nameAr: string | null;
}

export interface TaxType {
  code: string;
  taxable: boolean;
  nameEn: string;
  nameAr: string | null;
  subtypes: TaxSubtype[];
}

export interface RefCode {
  code: string;
  nameEn: string;
  nameAr: string | null;
}

// ----------------------------------------------------------------------------- parties
export type PartyType = 'B' | 'P' | 'F';

export interface Party {
  id: string;
  partyType: PartyType;
  isCustomer: boolean;
  isSupplier: boolean;
  taxId: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  categoryId: string | null;
  version: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Default address (list rows only). */
  address?: AddressView | null;
}

export interface PartyAddress {
  id: string;
  kind: string;
  country: string;
  governate: string;
  regionCity: string;
  street: string;
  buildingNumber: string;
  postalCode: string | null;
  floor: string | null;
  room: string | null;
  landmark: string | null;
  additionalInformation: string | null;
  isDefault: boolean;
  version: number;
}

export interface PartyDetail extends Party {
  addresses: PartyAddress[];
}

export interface AddressInput {
  country: string;
  governate: string;
  regionCity: string;
  street: string;
  buildingNumber: string;
  postalCode?: string;
  floor?: string;
  room?: string;
  landmark?: string;
  additionalInformation?: string;
}

export interface PartyInput {
  partyType: PartyType;
  isCustomer: boolean;
  isSupplier: boolean;
  taxId?: string;
  name: string;
  email?: string;
  phone?: string;
  notes?: string;
  categoryId?: string | null;
  address?: AddressInput;
}

// ----------------------------------------------------------------------------- items
export type ItemKind = 'goods' | 'service';
export type EtaItemType = 'GS1' | 'EGS';
export type TaxGoodsType = 'general' | 'table';
export type TableGoodsType = 'none' | 'table_1' | 'table_2';
export type StatementType = 'goods' | 'service' | 'adjustment';

export interface Item {
  id: string;
  internalCode: string;
  nameAr: string;
  nameEn: string | null;
  description: string | null;
  itemKind: ItemKind;
  isStockable: boolean;
  etaItemType: EtaItemType | null;
  etaItemCode: string | null;
  unitCode: string;
  unitNameEn: string;
  unitNameAr: string | null;
  unitAmbiguous: boolean;
  unitPrice: string;
  currency: string;
  taxGoodsType: TaxGoodsType;
  tableGoodsType: TableGoodsType;
  statementType: StatementType;
  categoryId: string | null;
  defaultDiscountRate: string | null;
  defaultDiscountAmount: string | null;
  defaultItemsDiscount: string | null;
  defaultValueDifference: string | null;
  /** List rows only. */
  defaultTaxIds?: string[];
  version: number;
  archivedAt: string | null;
}

export interface ItemDefaultTax {
  id: string;
  taxType: string;
  taxSubtype: string;
  rate: string | null;
  nameAr: string;
  nameEn: string;
}

export interface ItemDetail extends Item {
  defaultTaxes: ItemDefaultTax[];
}

export interface ItemInput {
  internalCode: string;
  nameAr: string;
  nameEn?: string;
  description?: string;
  itemKind: ItemKind;
  isStockable: boolean;
  etaItemType: EtaItemType;
  etaItemCode: string;
  unitCode: string;
  unitPrice: string;
  currency: string;
  taxGoodsType: TaxGoodsType;
  tableGoodsType: TableGoodsType;
  statementType: StatementType;
  defaultTaxIds: string[];
  categoryId?: string;
  defaultDiscount?: Discount;
  defaultItemsDiscount?: string;
  defaultValueDifference?: string;
}

// ----------------------------------------------------------------------------- sales documents
/** Business status. `discarded` = a deleted draft: kept, numbered and shown as void (OD-1 A).
 *  `signing`/`signed` are the e-seal step between `issued` and the ETA submission (WP-1b.1). */
export type DocumentStatus = 'draft' | 'issued' | 'signing' | 'signed' | 'superseded' | 'discarded';
/**
 * The six sales-document kinds: domestic and export invoices, and (Block 11) the domestic and
 * export credit and debit notes. `c`/`ec` are credit notes, `d`/`ed` debit notes.
 */
export const INVOICE_KINDS = ['i', 'ei', 'c', 'ec', 'd', 'ed'] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];
/** Narrows a server- or URL-supplied string to a known kind (anything else is not a kind). */
export const isInvoiceKind = (kind: string): kind is InvoiceKind =>
  (INVOICE_KINDS as readonly string[]).includes(kind);
export type Readiness = 'ready' | 'incomplete';
/** ETA axis (D-1 v2). `not_submitted` is the read model's "no submission yet". */
export type EtaStatus =
  | 'not_submitted'
  | 'submitting'
  | 'submitted'
  | 'valid'
  | 'invalid'
  | 'rejected'
  | 'not_accepted'
  | 'cancelled';
/** The tax-status filter values: readiness for drafts, the e-seal step, then the ETA axis. */
export type TaxStatus = Readiness | Exclude<EtaStatus, 'not_submitted'> | 'signing' | 'signed';
export type MockScenario =
  'valid' | 'invalid' | 'not_accepted' | 'rejected' | 'cancelled' | 'retry_wait' | 'unknown';
/** Block 11 (§9): the state-change scenarios of the mock, set by tests through the API only. */
export type MockStateScenario =
  | 'cancel_declined'
  | 'frozen'
  | 'state_forbidden'
  | 'state_unknown'
  | 'request_unseen'
  | 'hold_submitted';

export type Discount = { rate: string } | { amount: string };

export interface DraftLine {
  itemId?: string;
  description?: string;
  quantity?: string;
  /** In the document currency (OD-2 A). */
  unitPrice?: string;
  /** EGP. */
  discount?: Discount;
  itemsDiscount?: string;
  valueDifference?: string;
  taxIds?: string[];
  fixedTaxAmounts?: Record<string, string>;
  weightUnitType?: string;
  weightQuantity?: string;
  /** Block 11: the line of the referenced invoice this note line reduces (note kinds only). */
  referencedLineId?: string;
}

export interface Draft {
  branchId?: string;
  receiver?: { partyId: string } | { cash: true };
  issueDate?: string;
  dueDate?: string;
  serviceDeliveryDate?: string;
  activityCode?: string;
  currency: string;
  exchangeRate?: string;
  purchaseOrderRef?: string;
  purchaseOrderDescription?: string;
  salesOrderRef?: string;
  salesOrderDescription?: string;
  proformaInvoiceNumber?: string;
  extraDiscountAmount?: string;
  lines: DraftLine[];
  /** Block 11: the ids of the referenced invoices (note kinds only; the header locks to the first). */
  references?: string[];
}

export interface PreviewLineTax {
  taxType: string;
  subType: string;
  rate: string;
  amount: string;
}

export interface PreviewLine {
  amountEGP: string;
  salesTotal: string;
  discountRate: string;
  discountAmount: string;
  netTotal: string;
  totalTaxableFees: string;
  itemsDiscount: string;
  valueDifference: string;
  taxes: PreviewLineTax[];
  total: string;
}

export interface PreviewResult {
  engineVersion: string;
  roundingPolicy: string;
  totalSalesAmount: string;
  totalDiscountAmount: string;
  netAmount: string;
  totalItemsDiscountAmount: string;
  extraDiscountAmount: string;
  taxTotals: { taxType: string; amount: string }[];
  totalAmount: string;
  lines: PreviewLine[];
}

export interface Preview {
  readiness: Readiness;
  preview: {
    result?: PreviewResult;
    amountDueForeign: string | null;
    issues: ApiIssue[];
    warnings: ApiIssue[];
  };
}

export interface DraftSaved extends Preview {
  id: string;
  version: number;
  status: DocumentStatus;
  number: string | null;
  kind: InvoiceKind;
}

export interface CopyResult {
  id: string;
  version: number;
  number: string | null;
  copiedFrom: { id: string; number: string | null };
  copyWarnings: ApiIssue[];
}

/** The result of "correct and resend" (WP-1b.2, MF-INV-009): the replacement draft. */
export interface SupersedeResult {
  id: string;
  version: number;
  status: DocumentStatus;
  number: string | null;
  kind: InvoiceKind;
  supersedes: { id: string; number: string | null };
  copyWarnings: ApiIssue[];
  /** false: an open replacement draft already existed and is returned as it is. */
  created: boolean;
}

export interface SubmissionRequest {
  submissionId: string;
  status: string;
  environment: EtaEnvironment;
  alreadyRequested: boolean;
}

export interface IssuedResult {
  id: string;
  number: string;
  status: DocumentStatus;
  version: number;
  totalAmount: string;
  warnings: ApiIssue[];
  eta: SubmissionRequest;
}

/** The result of the separate signing step (WP-1b.1, MF-INV-007). */
export interface SignedResult {
  id: string;
  number: string | null;
  status: DocumentStatus;
  version: number;
  signature: {
    value: string;
    digest_sha256: string;
    signer_kind: string;
    signed_at: string | null;
    payloadId: string;
    alreadySigned: boolean;
  };
}

export interface SalesDocumentSummary {
  id: string;
  kind: string;
  status: DocumentStatus;
  number: string | null;
  receiverName: string | null;
  receiverId: string | null;
  receiverType: PartyType | null;
  cash: boolean;
  currency: string;
  exchangeRate: string | null;
  totalAmount: string | null;
  amountForeign: string | null;
  readiness: Readiness | null;
  etaStatus: EtaStatus | null;
  etaEnvironment: EtaEnvironment | null;
  date: string | null;
  lastActivityAt: string;
  createdAt: string;
  version: number;
  /**
   * Block 11 (T11-03): a rejected document's running objection period — `until` is the date, or
   * null when the period runs but its end is unknown. Null when no objection is possible.
   */
  declineWindow?: { until: string | null } | null;
  /**
   * Block 11 (RB3R11-11): a live cancellation the document is waiting on, so a valid/submitted row
   * never shows a plain «صالحة» while a cancellation is in flight, unconfirmed, or pending at ETA.
   */
  cancellation?: {
    state: 'requested' | 'unconfirmed' | 'pending';
    effectiveAt: string | null;
  } | null;
  /**
   * Block 11 (WP-11.4 Slice 8): the reference column of a note row — present on the credit and
   * debit tabs only, absent on the invoices tab (sales-notes.md §6). See `SalesReferenceColumn`.
   */
  reference?: SalesReferenceColumn | null;
}

// ─── WP-11.4 Slice 8: the reference column of the credit and debit tab registers ──────────────
/**
 * Block 11 (sales-notes.md §6, AC-T7): the note row's reference column — the first referenced
 * invoice's number and how many references in all. The register shows the first number and, when
 * `count` > 1, «+n» (n = `count` − 1) as plain text with an accessible name, never a trigger,
 * popover or menu. `first` is null when the first reference carries no local number (an ETA-only
 * import reference). Note rows only; the invoices tab never carries it.
 */
export interface SalesReferenceColumn {
  first: string | null;
  count: number;
}
// ──────────────────────────────────────────────────────────────────────────────────────────────

export interface AddressView {
  branchId?: string;
  country: string;
  governate: string;
  regionCity: string;
  street: string;
  buildingNumber: string;
  postalCode?: string | null;
  floor?: string | null;
  room?: string | null;
  landmark?: string | null;
  additionalInformation?: string | null;
}

export interface PartyView {
  type: PartyType;
  id?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  branchName?: string | null;
  address?: AddressView | null;
  partyId?: string | null;
  cash?: boolean;
}

export interface LineView {
  lineNo: number;
  itemId: string | null;
  description: string;
  itemType: string;
  itemCode: string;
  internalCode: string | null;
  unitType: string;
  quantity: string | null;
  unitPrice: string | null;
  amountEGP: string | null;
  discountRate: string | null;
  discountAmount: string | null;
  salesTotal: string | null;
  netTotal: string | null;
  itemsDiscount: string | null;
  valueDifference: string | null;
  totalTaxableFees: string | null;
  taxes: (PreviewLineTax & { organizationTaxId: string | null })[];
  total: string | null;
  weightUnitType: string | null;
  weightQuantity: string | null;
}

export interface TotalsView {
  totalSalesAmount: string;
  totalDiscountAmount: string;
  netAmount: string;
  taxTotals: { taxType: string; amount: string }[];
  totalItemsDiscountAmount: string;
  extraDiscountAmount: string;
  totalAmount: string;
  amountDue: string;
  amountDueForeign: string | null;
}

export interface TimelineEvent {
  seq: number;
  event: string;
  from: string | null;
  to: string | null;
  actorType: 'user' | 'system' | 'job';
  actorName: string | null;
  reason: string | null;
  at: string;
}

export interface EtaStateView {
  environment?: EtaEnvironment;
  status: EtaStatus;
  etaUuid?: string | null;
  longId?: string | null;
  statusReason?: string | null;
  validationResults?: {
    status?: string;
    mock?: boolean;
    validationSteps?: {
      name: string;
      status: string;
      error: { errorCode?: string; error?: string } | null;
    }[];
  } | null;
  lastSyncedAt?: string | null;
  validatedAt?: string | null;
  updatedAt?: string;
}

/**
 * One row of the Tax Authority's verdict on a refused document (WP-1b.2; migration 0019): a
 * validation step (depth 0) or an error under it (depth 1+), or the refusal at submission.
 */
export interface EtaResultRow {
  source: 'validation' | 'submission';
  etaStatus: 'invalid' | 'not_accepted';
  environment: EtaEnvironment;
  depth: number;
  stepName: string | null;
  stepStatus: string | null;
  errorCode: string | null;
  propertyPath: string | null;
  propertyName: string | null;
  messageEn: string | null;
  messageAr: string | null;
  recordedAt: string;
}

/** A bulk task on billing documents (WP-1b.3, MF-INV-008). */
export type BulkTask = 'send' | 'pdf' | 'discard';

/** One document's answer to a bulk call: its outcome, then the document after the action. */
export interface BulkResult {
  id: string;
  ok: boolean;
  /** When `ok` is false: the single action's error code. */
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
  number?: string | null;
  status?: DocumentStatus;
  etaStatus?: EtaStatus | null;
  readiness?: Readiness | null;
  version?: number;
  totalAmount?: string | null;
  receiverName?: string | null;
  cash?: boolean;
}

export interface BulkResponse {
  action: BulkTask;
  dryRun: boolean;
  results: BulkResult[];
}

/** Block 11: the four state changes at ETA (BLOCK-11-PLAN D-2). */
export type StateChangeAction = 'cancel' | 'reject' | 'decline_cancellation' | 'decline_rejection';
export type StateChangeStatus =
  | 'queued'
  | 'claimed'
  | 'sending'
  | 'done'
  | 'done_unconfirmed'
  | 'released'
  | 'refused'
  | 'unknown'
  | 'not_applied'
  | 'failed';

/** One state-change request of a document, as `GET …/eta` shows it. */
export interface StateChangeRequestView {
  id: string;
  action: StateChangeAction;
  status: StateChangeStatus;
  reason: string | null;
  confirmedAt: string | null;
  confirmReads: number;
  /** ETA's own code, kept verbatim (or the local reason of a refusal before sending). */
  etaErrorCode: string | null;
  /** The §7.3 code (ETA's code mapped, or the local reason). */
  code: string | null;
  refusalSource: 'local' | 'eta' | null;
  releaseRequestedAt: string | null;
  releasedAt: string | null;
  createdAt: string;
  sentAt: string | null;
}

export type WindowState = 'open' | 'uncertain' | 'closed' | 'unknown';

export interface WindowAnchor {
  source:
    | 'dateTimeIssued'
    | 'dateTimeReceived'
    | 'acceptedAt'
    | 'cancelRequestDate'
    | 'rejectRequestDate';
  clock: 'eta' | 'local';
  value: string;
  at: number;
}

/** One action's window, as the engine judged it (TD-2). */
export interface WindowView {
  action: StateChangeAction;
  state: WindowState;
  unknownReason: string | null;
  parameter: { name: string; basis: 'recorded' | 'mock_declared'; unit: 'hours' | 'days' } | null;
  candidates: { id: unknown; value: unknown; activeFrom: unknown; activeTo: unknown }[];
  anchors: { earliest: WindowAnchor | null; latest: WindowAnchor | null };
  earliestDeadline: string | null;
  latestDeadline: string | null;
  delayedDate: string | null;
  skewSeconds: number;
}

export interface ActionVerdictView {
  allowed: boolean;
  /** Why not (a local refusal of §7.3), or null. */
  reason: string | null;
  frozenWarning: boolean;
}

// ----------------------------------------------------------- WP-11.4 Slice 9: the cancel verdict's blocking notes
/**
 * Block 11 (D-13, F-17): a live note that references this invoice and therefore blocks its
 * cancellation. The server names them beside the verdict (`liveNotes`), so the band can list them
 * as links when `allowed.cancel.reason` is `active_referencing_documents`. Kept minimal — only what
 * a link needs (`liveNotesOf`).
 */
export interface BlockingNote {
  id: string;
  number: string | null;
  kind: string;
}

/** The Tax Authority's parameters as it sent them (verbatim). */
export interface WorkflowParameterView {
  id: unknown;
  parameter: unknown;
  value: unknown;
  activeFrom?: unknown;
  activeTo?: unknown;
  action?: unknown;
}

/** The Block 11 part of a document's ETA view; empty for a document ETA has not received. */
export interface StateChangeView {
  etaStateSeq: string | null;
  dateTimeReceived: string | null;
  acceptedAt: string | null;
  pending: {
    cancelRequestDate: string | null;
    cancelRequestDelayedDate: string | null;
    rejectRequestDate: string | null;
    rejectRequestDelayedDate: string | null;
    declineCancelRequestDate: string | null;
    declineRejectRequestDate: string | null;
  } | null;
  freeze: { frozen?: boolean | null; type?: number | null; scope?: number | null } | null;
  cancelPending: boolean;
  /**
   * G-3 (F-5; plan D-3): whether the buyer's own period to object to a cancellation has CERTAINLY
   * closed, from the same `windowOf('decline_cancellation', …) === 'closed'` predicate as the
   * timeline's two readings of `cancelled` (`eta-windows.ts` `cancellationSettled`). The band's
   * state line reads it, so the line and the history never disagree on the same read.
   */
  cancellationSettled: boolean;
  parameters: {
    snapshotId: string;
    documentType: string;
    fetchedAt: string;
    lastCheckedAt: string | null;
    mock: boolean;
    items: WorkflowParameterView[];
    /**
     * WP-11.3b (RB3R11-02): the action each `items[i]` governs, computed by the server (TD-1),
     * aligned with `items`; environment-agnostic, so the band groups every parameter under its own
     * action even where ETA's raw rows carry no `action` (ETA-W7). A row that governs nothing is
     * `null`.
     */
    meta?: { governs: StateChangeAction | null; unit: 'hours' | 'days' | null }[];
  } | null;
  windows: { cancel: WindowView; decline_rejection: WindowView } | null;
  allowed: { cancel: ActionVerdictView; decline_rejection: ActionVerdictView } | null;
  requests: StateChangeRequestView[];
  /**
   * Block 11 (D-13, F-17): the live notes referencing this invoice; non-empty only when they block
   * its cancellation (then `allowed.cancel.reason` is `active_referencing_documents`). The band
   * names them as links (see `BlockingNote`).
   */
  liveNotes: BlockingNote[];
  watch: {
    until: string | null;
    ended: boolean;
    detailsSyncedAt: string | null;
    lastSyncedAt: string | null;
    checkRequestedAt: string | null;
  } | null;
}

/** The ETA sub-resource of a document (`GET …/:id/eta`). */
export interface EtaStatusDetail extends Partial<StateChangeView> {
  state: EtaStateView;
  submissions: SubmissionView[];
  results: EtaResultRow[];
}

export interface SubmissionView {
  id: string;
  status: string;
  environment: EtaEnvironment;
  submissionUuid: string | null;
  attempts: number;
  lastErrorCode: string | null;
  result: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Block 11 (D-7, AC-T8): a referenced invoice on a note's detail page, in the note's own order
 * (number · Tax Authority identifier · total · date). The server sends these; Slice 9 renders them.
 */
export interface RelatedReference {
  id: string;
  kind: string;
  number: string | null;
  status: DocumentStatus;
  totalAmount: string | null;
  dateTimeIssued: string | null;
  issueDate: string | null;
  etaUuid: string | null;
  etaStatus: EtaStatus | null;
}

/** Block 11 (D-11): a note on an invoice's detail page, with its state. */
export interface RelatedNote {
  id: string;
  kind: string;
  number: string | null;
  status: DocumentStatus;
  totalAmount: string | null;
  etaStatus: EtaStatus | null;
}

export interface SalesDocumentDetail {
  document: {
    id: string;
    kind: string;
    status: DocumentStatus;
    number: string | null;
    internalId: string | null;
    version: number;
    currency: string;
    exchangeRate: string | null;
    activityCode: string | null;
    activity: { code: string; nameAr: string | null; nameEn: string } | null;
    branch: { id: string; name: string; etaBranchId: string } | null;
    issueDate: string | null;
    dateTimeIssued: string | null;
    dueDate: string | null;
    serviceDeliveryDate: string | null;
    purchaseOrderRef: string | null;
    purchaseOrderDescription: string | null;
    salesOrderRef: string | null;
    salesOrderDescription: string | null;
    proformaInvoiceNumber: string | null;
    createdAt: string;
    updatedAt: string;
    discardedAt: string | null;
    copiedFrom: { id: string; number: string | null; status: DocumentStatus } | null;
    copyWarnings: ApiIssue[];
    copies: { id: string; number: string | null; status: DocumentStatus }[];
    /** WP-1b.2: the refused document this one replaces, and this one's live replacement. */
    supersedes: { id: string; number: string | null; status: DocumentStatus } | null;
    supersededBy: { id: string; number: string | null; status: DocumentStatus } | null;
    supersededAt: string | null;
    /** The state rule only (ETA found it invalid or did not accept it); permission is separate. */
    canSupersede: boolean;
  };
  issuer: PartyView & { address?: AddressView | null };
  receiver: PartyView | null;
  lines: LineView[];
  totals: TotalsView | null;
  readiness: { readiness: Readiness; issues: ApiIssue[]; warnings: ApiIssue[] } | null;
  draft: Draft | null;
  events: TimelineEvent[];
  eta: EtaStatusDetail | null;
  /** WP-1b.4: null when no copy was requested at issue (and on drafts). */
  delivery: DeliveryView | null;
  /** Block 11: a note's referenced invoices, in the note's own order (empty for an invoice). */
  references: RelatedReference[];
  /** Block 11: an invoice's credit/debit notes (empty for a note). */
  notes: RelatedNote[];
  /** Block 11 (D-11): what a credit note may still reduce on a valid invoice; null otherwise. */
  reducible: string | null;
}

// ----------------------------------------------------------------------------- notes (Block 11)
/**
 * `GET …/sales/note-sources` (D-3): one invoice a note of the asked kind may reference. The option
 * text follows AC-N7: number · total · remaining · date · identifier. `etaId` is the Tax Authority
 * identifier, shortened (never the full uuid in a list); `remaining` is the credit cap (null for a
 * debit note).
 */
export interface NoteSource {
  id: string;
  kind: string;
  number: string | null;
  receiverName: string | null;
  receiverId: string | null;
  receiverPartyId: string | null;
  receiverType: PartyType | null;
  currency: string;
  totalAmount: string | null;
  dateTimeIssued: string | null;
  issueDate: string | null;
  etaId: string | null;
  remaining: string | null;
}

export interface NoteSources {
  items: NoteSource[];
}

/** A tax of a referenced line (D-9: the locked tax identity). */
export interface NoteBasisLineTax {
  taxType: string;
  subType: string;
  rate: string;
  amount: string;
  organizationTaxId: string | null;
  kind: string | null;
}

/** A line of a referenced invoice, for the reference-lines sheet (note editor step 3). */
export interface NoteBasisLine {
  id: string;
  lineNo: number;
  description: string;
  itemType: string;
  itemCode: string;
  internalCode: string | null;
  unitType: string;
  quantity: string;
  /** In the invoice's currency. */
  unitPrice: string;
  weightUnitType: string | null;
  weightQuantity: string | null;
  taxes: NoteBasisLineTax[];
}

/**
 * `GET …/sales/documents/:id/note-basis` (D-9, D-11): what a note of the asked kind may take from
 * one invoice — its lines (the reference-lines sheet) and, for a credit note, its cap. `eligibility`
 * is the blocking reason this invoice may not be referenced now, or null if it may.
 */
export interface NoteBasis {
  document: {
    id: string;
    kind: string;
    number: string | null;
    currency: string;
    exchangeRate: string | null;
    receiverName: string | null;
    receiverId: string | null;
    receiverPartyId: string | null;
    /** D-2: the reference's own branch and activity code, which a note locks to (step 2 shows
     *  these real values, not the unsaved draft's org defaults, under the «من الفاتورة المرجعية» tag). */
    branchId: string | null;
    activityCode: string | null;
    totalAmount: string | null;
    dateTimeIssued: string | null;
    serviceDeliveryDate: string | null;
    etaUuid: string | null;
  };
  lines: NoteBasisLine[];
  cap: {
    total: string | null;
    otherLiveCredits: { id: string; number: string | null; totalAmount: string }[];
    remaining: string;
  } | null;
  eligibility: ApiIssue | null;
  warnings: ApiIssue[];
}

export interface OrgActivityCode {
  code: string;
  isPrimary: boolean;
  nameAr: string | null;
  nameEn: string;
}

export interface OrgCurrency {
  code: string;
  isPrimary: boolean;
  nameAr: string | null;
  nameEn: string;
}

export interface Category {
  id: string;
  scope: 'item' | 'party';
  nameAr: string;
  nameEn: string | null;
  version: number;
}

export interface EtaItemCode {
  id: string;
  itemType: EtaItemType;
  itemCode: string;
  description: string | null;
}

// ----------------------------------------------------------------------------- e-receipts
/** Business status of a receipt. `discarded` is a deleted draft (kept and numbered). */
export type ReceiptStatus = 'draft' | 'issued' | 'discarded';
/** ETA axis of a receipt (D-1 v2). A draft has no ETA row until it is issued. */
export type ReceiptEtaStatus =
  'submitting' | 'submitted' | 'valid' | 'invalid' | 'rejected' | 'cancelled' | 'not_accepted';

export interface ReceiptSummary {
  id: string;
  status: ReceiptStatus;
  receiptNumber: string | null;
  receiptType: string;
  uuid: string | null;
  previousUuid: string | null;
  buyerType: PartyType;
  buyerName: string | null;
  currency: string;
  totalAmount: string | null;
  posDeviceId: string;
  branchId: string;
  version: number;
  createdAt: string;
  etaStatus: ReceiptEtaStatus | null;
  etaEnvironment: EtaEnvironment | null;
  /** Invalid because the Tax Authority refused it at submission (RB3R8-04). */
  etaNotAccepted?: boolean;
  date: string | null;
  lastActivityAt: string;
}

/** A receipt draft line as typed in the editor; the server resolves items and taxes at issue. */
export interface ReceiptLineDraft {
  itemId?: string;
  description?: string;
  quantity?: string;
  /** In the receipt currency; never recomputed in the browser. */
  unitPrice?: string;
  taxIds?: string[];
  /** A fixed fee's amount for the whole line (fixed subtypes only), keyed by organization tax id. */
  fixedTaxAmounts?: Record<string, string>;
}

/**
 * The receipt draft payload (ETA receipt v1.2). It is the source of truth stored server-side; the
 * header is a denormalized copy. Amounts are not part of it — the server calculates them.
 */
export interface ReceiptDraftPayload {
  branchId?: string;
  posDeviceId?: string;
  receiptType: string;
  currency: string;
  exchangeRate?: string;
  paymentMethod?: string;
  activityCode?: string;
  buyer?: { type: PartyType; id?: string; name?: string; mobile?: string };
  dateTimeIssued?: string;
  issueDate?: string;
  documentUseReason?: 'I' | 'B';
  referenceUUID?: string;
  referenceOldUUID?: string;
  salesIssuedDateTime?: string;
  origin?: 'ctax' | 'pos' | 'import';
  lines: ReceiptLineDraft[];
}

export interface ReceiptSaved {
  id: string;
  version: number;
  status: ReceiptStatus;
  receiptNumber: string;
  branchId: string;
  posDeviceId: string;
}

export interface ReceiptIssued {
  id: string;
  receiptNumber: string;
  status: ReceiptStatus;
  version: number;
  uuid: string;
  previousUuid: string | null;
  totalAmount: string;
  issueDateLocal: string;
  chain: { posDeviceId: string; lastUuid: string; lastReceiptNumber: string };
  eta: {
    submissionId: string;
    status: string;
    environment: EtaEnvironment;
    alreadyRequested: boolean;
  };
}

export interface ReceiptLineView {
  lineNo: number;
  itemId: string | null;
  description: string;
  itemType: string;
  itemCode: string;
  internalCode: string | null;
  unitType: string;
  quantity: string | null;
  unitPrice: string | null;
  netSale: string | null;
  totalSale: string | null;
  total: string | null;
  commercialDiscount: string | null;
  itemDiscount: string | null;
  additionalCommercialDiscount: string | null;
  additionalItemDiscount: string | null;
  valueDifference: string | null;
  taxes: { taxType: string; taxSubtype: string; rate: string | null; amount: string }[];
}

/** One in-service POS device, for the receipt editor's picker (WP-8.9c). Never credentials. */
export interface PosDevice {
  id: string;
  name: string;
  serial: string;
  branchId: string;
  branchName: string;
}

/**
 * A draft's amounts as the issue would compute them now (the server's own resolution and engine,
 * WP-8.9c): in the draft's currency; `totals` is null while nothing can be computed.
 */
export interface ReceiptPreview {
  currency: string;
  lines: { totalSale: string; netSale: string; total: string }[];
  taxTotals: { taxType: string; amount: string }[];
  totals: {
    totalSales: string;
    totalCommercialDiscount: string;
    totalItemsDiscount: string;
    netAmount: string;
    extraReceiptDiscount: string;
    totalAmount: string;
  } | null;
  issues: ApiIssue[];
}

export interface ReceiptDetail {
  receipt: {
    id: string;
    status: ReceiptStatus;
    receiptNumber: string | null;
    receiptType: string;
    typeVersion: string | null;
    uuid: string | null;
    previousUuid: string | null;
    referenceUuid: string | null;
    referenceOldUuid: string | null;
    documentUseReason: string | null;
    salesIssuedDateTime: string | null;
    dateTimeIssued: string | null;
    issueDateLocal: string | null;
    currency: string;
    exchangeRate: string | null;
    buyerType: PartyType;
    buyerId: string | null;
    buyerName: string | null;
    buyerMobile: string | null;
    paymentMethod: string | null;
    activityCode: string | null;
    branchId: string;
    posDeviceId: string;
    origin: string;
    totals: {
      totalSales: string | null;
      totalCommercialDiscount: string | null;
      totalItemsDiscount: string | null;
      extraReceiptDiscount: string | null;
      netAmount: string | null;
      feesAmount: string | null;
      totalAmount: string | null;
      adjustment: string | null;
    };
    version: number;
    createdAt: string;
    updatedAt: string;
    discardedAt: string | null;
  };
  draft: ReceiptDraftPayload | null;
  /** A draft's amounts now (null once issued: the receipt's own amounts are stored). */
  preview: ReceiptPreview | null;
  device: { id: string; name: string; serial: string; branchId: string } | null;
  branch: { id: string; name: string; etaBranchId: string } | null;
  lines: ReceiptLineView[];
  taxTotals: { taxType: string; amount: string }[];
  chain: { lastUuid: string; lastReceiptNumber: string; updatedAt: string } | null;
  events: TimelineEvent[];
  eta: {
    environment: EtaEnvironment;
    status: ReceiptEtaStatus;
    etaUuid: string | null;
    longId: string | null;
    statusReason: string | null;
    validationResults: {
      status?: string;
      mock?: boolean;
      validationSteps?: {
        name: string;
        status: string;
        error: { errorCode?: string; error?: string } | null;
      }[];
    } | null;
    lastSyncedAt: string | null;
    validatedAt: string | null;
    updatedAt: string;
  } | null;
}

// ----------------------------------------------------------------------------- audit
export interface AuditEntry {
  id: string;
  occurredAt: string;
  action: string;
  entityType: string;
  entityId: string | null;
  result: string;
  actorUserId: string | null;
  actorName: string | null;
  requestId: string | null;
  changes: Record<string, unknown> | null;
}
